import React, { useRef, useState } from "react";
import { Upload, X, Check, AlertTriangle, Loader2, FileText } from "lucide-react";
import { Paciente } from "../../../types";
import { useExames } from "../context/ExamesContext";
import { useMarcadores } from "../../marcadores/context/MarcadoresContext";
import { useSorologias } from "../../sorologias/context/SorologiasContext";
import { callAnalyze } from "../../../lib/analyzeClient";
import { calcularMELD } from "../../../utils/meld";
import { ehDataFutura, msgDataFutura, hojeISO } from "../../../shared/utils/dataNaoFutura";
import { avaliarDataExame } from "../../../shared/utils/dataExameSuspeita";
import { rotuloCampoLab, parseValorLab } from "../data/camposExamesLab";
import { rotuloCampoMarcador } from "../data/camposMarcadores";
import { rotuloCampoSorologia } from "../data/camposSorologias";
// B247 (AI_111): padronizar resultados de sorologia/autoanticorpo na importação,
// para a CONFERÊNCIA já mostrar o valor padronizado (Positivo→Reagente etc.).
import {
  normalizarSorologia,
  normalizarAutoanticorpo,
  padronizarRegistroSorologia,
  padronizarRegistroMarcadores,
  TIPO_AUTOANTICORPO,
} from "../../sorologias/services/normalizarSorologia";
import { recusarSePdfInvalido } from "../../../shared/utils/arquivoPdf";

// ============================================================
// B241 (AI_108) — IMPORTAR EXAMES DE PDF (entrada única em Exames Lab)
// ------------------------------------------------------------
// FASE 2 (AI_109): um único laudo pode trazer LABORATÓRIO + MARCADORES +
// SOROLOGIAS juntos. O backend separa cada resultado no seu bucket; a TELA
// DE CONFERÊNCIA mostra as três seções por data; ao salvar, cada bucket vai
// para a sua coleção (exames / marcadores / sorologias). NADA é salvo sem a
// confirmação do médico (governança §2.3). A IA só LÊ/propõe; quem grava é o
// médico, e os valores vão direto do PDF, não são inventados.
// ============================================================

type Bucket = "lab" | "marcadores" | "sorologias";

interface ValorExtraido {
  campo: string;
  valorTexto: string;
  unidadeDetectada: string;
  textoOriginal: string;
}
interface ExameExtraido {
  data: string;
  valores: ValorExtraido[];
  valoresMarcadores?: ValorExtraido[];
  valoresSorologias?: ValorExtraido[];
}
interface PacienteVerificacao {
  nomeDetectado: string;
  confere: boolean;
  observacao: string;
}
interface ResultadoExtracao {
  exames: ExameExtraido[];
  naoMapeados: { nome: string; valor: string; unidade: string }[];
  aviso: string;
  pacienteVerificacao?: PacienteVerificacao;
  erro?: string;
}

// Linha editável da conferência
interface LinhaConf {
  bucket: Bucket;
  campo: string;
  label: string;
  unidade: string;
  valorTexto: string;
  textoOriginal: string;
  incluir: boolean;
}
interface GrupoConf {
  data: string;
  linhas: LinhaConf[];
  /**
   * B341 (AI_150) — a extração NÃO trouxe data legível e o campo foi preenchido
   * com HOJE só para o formulário ter um valor. Precisa ser conferido: gravar a
   * data de hoje no lugar da data real do exame é erro silencioso (o registro
   * entra na ordem errada e, no pós-Tx, com o PO errado).
   */
  dataAusente?: boolean;
}

// Metadados de cada bucket para o roteamento e a renderização.
const BUCKETS: { bucket: Bucket; titulo: string; numerico: boolean }[] = [
  { bucket: "lab", titulo: "Laboratório", numerico: true },
  { bucket: "marcadores", titulo: "Marcadores", numerico: false },
  { bucket: "sorologias", titulo: "Sorologias", numerico: false },
];

// B247: padroniza o valor extraído por bucket/campo (mantém o original se não
// reconhecer — nunca perde dado da importação). Lab fica intacto (numérico).
function padronizarValorImport(bucket: Bucket, campo: string, valor: string): string {
  if (bucket === "sorologias") return normalizarSorologia(campo, valor) ?? valor;
  if (bucket === "marcadores" && TIPO_AUTOANTICORPO[campo])
    return normalizarAutoanticorpo(campo, valor) ?? valor;
  return valor;
}

function dataHojeISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

interface Props {
  paciente: Paciente;
  isPosTx: boolean;
  onFechar: () => void;
  onImportado: () => void;
}

export default function ImportarExamesLabModal({
  paciente,
  isPosTx,
  onFechar,
  onImportado,
}: Props) {
  const { adicionarExame } = useExames();
  const { adicionarMarcador } = useMarcadores();
  const { adicionarSorologia } = useSorologias();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fase, setFase] = useState<
    "upload" | "carregando" | "conferencia" | "salvando" | "sucesso"
  >("upload");
  const [resultadoSalvo, setResultadoSalvo] = useState<{
    exames: number;
    valores: number;
  } | null>(null);
  const [erro, setErro] = useState<string>("");
  const [aviso, setAviso] = useState<string>("");
  const [naoMapeados, setNaoMapeados] = useState<ResultadoExtracao["naoMapeados"]>([]);
  const [grupos, setGrupos] = useState<GrupoConf[]>([]);
  const [nomeArquivo, setNomeArquivo] = useState<string>("");
  // B241 (AI_109) — alerta NÃO-bloqueante quando o nome do PDF não confere
  // com o paciente selecionado (Haiku faz a comparação, tolerante).
  const [alertaNome, setAlertaNome] = useState<PacienteVerificacao | null>(null);
  // B341 — índices de grupo cuja data suspeita o médico já conferiu (destrava o
  // salvar; o aviso continua visível). Fica junto dos demais estados de propósito.
  const [datasConfirmadas, setDatasConfirmadas] = useState<number[]>([]);

  const lerBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const r = String(reader.result || "");
        const idx = r.indexOf(",");
        resolve(idx >= 0 ? r.slice(idx + 1) : r);
      };
      reader.onerror = () => reject(new Error("Falha ao ler o arquivo"));
      reader.readAsDataURL(file);
    });

  const onSelecionarArquivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // B366 (AI_156) — valida pelo CONTEÚDO, não por `file.type`. O navegador
    // deixa `file.type` VAZIO em PDF vindo de app de digitalização, WhatsApp,
    // nuvem ou alguns celulares — e a tela recusava um PDF legítimo com
    // "Selecione um arquivo PDF." (relato da equipe de Rondônia, 29/10). Ler os
    // bytes é mais forte que o teste antigo E independe do aparelho: o servidor
    // não confere o conteúdo, só o tamanho.
    setErro("");
    setNomeArquivo(file.name);
    setFase("carregando");
    try {
      const base64 = await lerBase64(file);
      const recusa = recusarSePdfInvalido(file, base64);
      if (recusa) {
        setErro(recusa);
        setFase("upload");
        return;
      }
      const res = await callAnalyze<ResultadoExtracao>({
        etapa: "extrair-exames-lab",
        file: base64,
        fileType: "application/pdf",
        // B241 (AI_109): nome esperado p/ o Haiku conferir contra o PDF.
        nomePacienteEsperado: paciente.nome,
      });
      if (res.erro) {
        setErro(
          "Não foi possível ler o PDF automaticamente. Você pode lançar os valores manualmente."
        );
        setFase("upload");
        return;
      }
      // Constrói as linhas de um bucket a partir da lista de valores extraída.
      const montarLinhas = (
        valores: ValorExtraido[] | undefined,
        bucket: Bucket,
        rotulo: (campo: string) => { label: string; unidade: string }
      ): LinhaConf[] =>
        (valores || []).map((v) => {
          const r = rotulo(v.campo);
          return {
            bucket,
            campo: v.campo,
            label: r.label,
            unidade: r.unidade || v.unidadeDetectada || "",
            valorTexto: padronizarValorImport(bucket, v.campo, v.valorTexto || ""),
            textoOriginal: v.textoOriginal || "",
            incluir: true,
          };
        });

      const novosGrupos: GrupoConf[] = (res.exames || []).map((ex) => ({
        data: ex.data && /^\d{4}-\d{2}-\d{2}$/.test(ex.data) ? ex.data : dataHojeISO(),
        // B341 — o prompt agora manda deixar a data VAZIA quando o ano está
        // ilegível (melhor que chutar o século). Sem esta marca, o fallback
        // abaixo gravaria HOJE sem ninguém perceber.
        dataAusente: !(ex.data && /^\d{4}-\d{2}-\d{2}$/.test(ex.data)),
        linhas: [
          ...montarLinhas(ex.valores, "lab", rotuloCampoLab),
          ...montarLinhas(ex.valoresMarcadores, "marcadores", rotuloCampoMarcador),
          ...montarLinhas(ex.valoresSorologias, "sorologias", rotuloCampoSorologia),
        ],
      }));
      // B341 — conferências de data são POR ÍNDICE de grupo: ao montar um novo
      // conjunto (outro PDF na mesma sessão do modal), zera para não destravar
      // um grupo novo com a confirmação de um grupo antigo.
      setDatasConfirmadas([]);
      // Remove grupos (datas) que não tenham nenhuma linha em nenhum bucket.
      const gruposComLinhas = novosGrupos.filter((g) => g.linhas.length > 0);
      setGrupos(gruposComLinhas);
      setNaoMapeados(res.naoMapeados || []);
      setAviso(res.aviso || "");
      // B241 (AI_109): só alarma quando confere===false (default permissivo).
      const pv = res.pacienteVerificacao;
      setAlertaNome(pv && pv.confere === false ? pv : null);
      if (gruposComLinhas.length === 0) {
        setErro(
          "Nenhum resultado reconhecido neste PDF. Confira se é um laudo legível (laboratório, marcadores ou sorologias)."
        );
        setFase("upload");
        return;
      }
      setFase("conferencia");
    } catch (err) {
      console.error("[ImportarExamesLab] erro:", err);
      // B243 (AI_110): distingue falta de permissão (403) de erro técnico, para
      // não confundir o usuário com "Erro ao processar o PDF" quando na verdade
      // o perfil dele não tem acesso à importação por IA.
      const msg = String(err);
      if (msg.includes("HTTP 403")) {
        setErro(
          "Seu usuário não tem permissão para importar exames por PDF. Fale com o administrador."
        );
      } else {
        setErro("Erro ao processar o PDF. Tente novamente.");
      }
      setFase("upload");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const setData = (gi: number, data: string) =>
    setGrupos((gs) =>
      // B341 — ao digitar a data, ela deixa de ser "não lida do PDF".
      gs.map((g, i) => (i === gi ? { ...g, data, dataAusente: false } : g))
    );
  const setValor = (gi: number, li: number, valorTexto: string) =>
    setGrupos((gs) =>
      gs.map((g, i) =>
        i === gi
          ? { ...g, linhas: g.linhas.map((l, j) => (j === li ? { ...l, valorTexto } : l)) }
          : g
      )
    );
  const toggleIncluir = (gi: number, li: number) =>
    setGrupos((gs) =>
      gs.map((g, i) =>
        i === gi
          ? {
              ...g,
              linhas: g.linhas.map((l, j) =>
                j === li ? { ...l, incluir: !l.incluir } : l
              ),
            }
          : g
      )
    );

  // Uma linha é "salvável" se está marcada E tem valor utilizável:
  //  - LAB: precisa virar número (parseValorLab); qualificadores/texto = inválido.
  //  - MARCADORES/SOROLOGIAS: valor é TEXTO; basta não estar vazio.
  const linhaValida = (l: LinhaConf): boolean => {
    if (!l.incluir) return false;
    if (l.bucket === "lab") return parseValorLab(l.valorTexto) !== null;
    return l.valorTexto.trim() !== "";
  };

  const totalIncluidos = grupos.reduce(
    (s, g) => s + g.linhas.filter(linhaValida).length,
    0
  );

  // 📅 B341 (AI_150) — plausibilidade da DATA lida do PDF. Caso real: folhão
  // manuscrito com `31/07/26` extraído como 2006 → registro 20 anos antes do
  // transplante, fora da ordem do Folhão e sem PO. A data é o campo que mais
  // passa batido na conferência (fica no cabeçalho, no meio de dezenas de
  // valores), então aqui ela é DESTACADA e o salvar fica travado até o médico
  // confirmar ou corrigir (decisão de Leonardo).
  const dataTransplantePaciente =
    paciente?.inscricaoTx?.dataTransplante || paciente?.dataTransplante || null;
  const avaliacoesData = grupos.map((g) =>
    g.dataAusente
      ? {
          suspeita: true,
          mensagem:
            "A data da coleta não foi lida do PDF — o campo está com a data de HOJE. Informe a data real do exame.",
          sugestao: undefined,
        }
      : avaliarDataExame({
          data: g.data,
          dataTransplante: dataTransplantePaciente,
          dataNascimento: paciente?.dataNascimento || null,
          contextoPosTx: isPosTx,
        })
  );
  const haDataSuspeita = avaliacoesData.some((a) => a.suspeita);
  const pendenteConferirData = avaliacoesData.some(
    (a, i) => a.suspeita && !datasConfirmadas.includes(i)
  );
  // Corrigir a data limpa a confirmação daquele grupo: a nova data é reavaliada.
  const aplicarSugestaoData = (gi: number, sugestao: string) => {
    setData(gi, sugestao);
    setDatasConfirmadas((c) => c.filter((i) => i !== gi));
  };

  const salvar = async () => {
    // 📅 B296 — não importar lote com data futura (qualquer grupo). Bloqueia ANTES
    // de gravar para não salvar parcialmente; permite hoje/retroativo.
    if (grupos.some((g) => ehDataFutura(g.data))) {
      alert(msgDataFutura("Data da coleta"));
      return;
    }
    // 📅 B341 — data implausível ainda não conferida. O botão já fica desabilitado;
    // esta é a defesa em profundidade (o estado pode mudar entre o render e o
    // clique). Nunca grava sem o médico ter olhado a data.
    if (pendenteConferirData) {
      alert(
        "Confira a data destacada em vermelho: ela não é compatível com este paciente. Corrija a data ou marque \"A data está correta\" antes de salvar."
      );
      return;
    }
    setFase("salvando");
    let docsSalvos = 0;
    let valoresSalvos = 0;
    try {
      for (const g of grupos) {
        // LAB → coleção exames (valores numéricos).
        const camposLab: Record<string, number> = {};
        // MARCADORES → coleção marcadores (valores texto).
        const camposMarc: Record<string, string> = {};
        // SOROLOGIAS → coleção sorologias (valores texto).
        const camposSoro: Record<string, string> = {};
        for (const l of g.linhas) {
          if (!linhaValida(l)) continue;
          if (l.bucket === "lab") {
            camposLab[l.campo] = parseValorLab(l.valorTexto) as number;
          } else if (l.bucket === "marcadores") {
            camposMarc[l.campo] = l.valorTexto.trim();
          } else {
            camposSoro[l.campo] = l.valorTexto.trim();
          }
        }

        const nLab = Object.keys(camposLab).length;
        if (nLab > 0) {
          const exameLab: Record<string, any> = {
            data: g.data,
            // B241: a CONFERÊNCIA já aconteceu no modal → o exame entra SALVO
            // (editavel:false), sem exigir outro clique em "Salvar" na tabela.
            editavel: false,
            ...(isPosTx ? { tipoExame: "pos" as const } : {}),
            ...camposLab,
          };
          // B248 (AI_111): calcular MELD 3.0 no IMPORT. A entrada manual calculava
          // por célula (handleSalvar); o import gravava sem MELD → vinha "-".
          if (
            camposLab.creatinina &&
            camposLab.bilirrubinaTotal &&
            camposLab.inr &&
            camposLab.sodio &&
            camposLab.albumina
          ) {
            exameLab.meld = calcularMELD({
              creatinina: camposLab.creatinina,
              bilirrubina: camposLab.bilirrubinaTotal,
              inr: camposLab.inr,
              sodio: camposLab.sodio,
              albumina: camposLab.albumina,
              sexoFeminino: paciente.sexo === "F",
            }).score;
          }
          await adicionarExame(paciente.id, exameLab as any);
          docsSalvos += 1;
          valoresSalvos += nLab;
        }

        const nMarc = Object.keys(camposMarc).length;
        if (nMarc > 0) {
          await adicionarMarcador(
            paciente.id,
            padronizarRegistroMarcadores(
              { dataColeta: g.data, ...camposMarc },
              { manterOriginal: true }
            ) as any
          );
          docsSalvos += 1;
          valoresSalvos += nMarc;
        }

        const nSoro = Object.keys(camposSoro).length;
        if (nSoro > 0) {
          await adicionarSorologia(
            paciente.id,
            padronizarRegistroSorologia(
              { dataColeta: g.data, ...camposSoro },
              { manterOriginal: true }
            ) as any
          );
          docsSalvos += 1;
          valoresSalvos += nSoro;
        }
      }
      // Dispara o refresh da tabela (não-bloqueante) e mostra a tela de sucesso.
      // NÃO usar alert() aqui: bloqueia a thread e atrasava o re-render da tabela.
      onImportado();
      setResultadoSalvo({ exames: docsSalvos, valores: valoresSalvos });
      setFase("sucesso");
    } catch (err) {
      console.error("[ImportarExamesLab] erro ao salvar:", err);
      setErro("Erro ao salvar os exames. Tente novamente.");
      setFase("conferencia");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={onFechar}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white",
          borderRadius: "14px",
          width: "min(900px, 96vw)",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
        }}
      >
        {/* CABEÇALHO */}
        <div
          style={{
            padding: "16px 20px",
            background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <FileText size={20} />
            <strong style={{ fontSize: "16px" }}>Importar exames de PDF</strong>
          </div>
          <button
            onClick={onFechar}
            style={{ background: "none", border: "none", color: "white", cursor: "pointer" }}
          >
            <X size={22} />
          </button>
        </div>

        <div style={{ padding: "20px", overflowY: "auto" }}>
          {erro && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                color: "#991b1b",
                borderRadius: "8px",
                padding: "10px 12px",
                marginBottom: "14px",
                fontSize: "14px",
              }}
            >
              {erro}
            </div>
          )}

          {/* FASE UPLOAD */}
          {fase === "upload" && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <p style={{ color: "#444", fontSize: "14.5px", marginBottom: "16px", lineHeight: 1.5 }}>
                Selecione o <strong>PDF do laudo</strong> do paciente (laboratório, marcadores
                e/ou sorologias — pode ser tudo no mesmo arquivo). A IA lê os resultados,
                separa cada um no seu lugar e você confere antes de gravar.
              </p>
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                onChange={onSelecionarArquivo}
                style={{ display: "none" }}
              />
              <button
                onClick={() => inputRef.current?.click()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "12px 22px",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <Upload size={18} /> Escolher PDF
              </button>
              <p style={{ color: "#9ca3af", fontSize: "12px", marginTop: "12px" }}>
                Os valores extraídos são uma sugestão. Confira sempre antes de salvar.
              </p>
            </div>
          )}

          {/* FASE CARREGANDO */}
          {fase === "carregando" && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#2563eb" }}>
              <Loader2 size={36} style={{ animation: "spin 1s linear infinite" }} />
              <p style={{ marginTop: "12px", color: "#444", fontSize: "14px" }}>
                Lendo {nomeArquivo || "o PDF"}…
              </p>
              <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
            </div>
          )}

          {/* FASE SUCESSO (não-bloqueante) */}
          {fase === "sucesso" && resultadoSalvo && (
            <div style={{ textAlign: "center", padding: "30px 10px" }}>
              <div style={{ color: "#16a34a", marginBottom: "10px" }}>
                <Check size={40} />
              </div>
              <p
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#166534",
                  marginBottom: "6px",
                }}
              >
                {resultadoSalvo.valores} valor(es) salvos em {resultadoSalvo.exames}{" "}
                registro(s) (laboratório, marcadores e/ou sorologias).
              </p>
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "13.5px",
                  marginBottom: "20px",
                }}
              >
                Os exames já aparecem na tabela, editáveis. Confira e ajuste se
                necessário.
              </p>
              <button
                onClick={onFechar}
                style={{
                  padding: "11px 28px",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Fechar
              </button>
            </div>
          )}

          {/* FASE CONFERÊNCIA */}
          {(fase === "conferencia" || fase === "salvando") && (
            <div>
              {/* B241 (AI_109) — ALERTA DE NOME (não-bloqueante): o nome do
                  PDF não parece ser o do paciente selecionado. Não impede
                  salvar; serve para o médico confirmar que é o laudo certo. */}
              {alertaNome && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    background: "#fef2f2",
                    border: "2px solid #ef4444",
                    color: "#991b1b",
                    borderRadius: "10px",
                    padding: "12px 14px",
                    marginBottom: "14px",
                    fontSize: "13.5px",
                    lineHeight: 1.5,
                  }}
                >
                  <AlertTriangle size={20} style={{ flexShrink: 0, marginTop: "1px" }} />
                  <div>
                    <strong>Atenção: o nome no PDF não confere com o paciente selecionado.</strong>
                    <div style={{ marginTop: "4px" }}>
                      No PDF:{" "}
                      <strong>
                        {alertaNome.nomeDetectado
                          ? `"${alertaNome.nomeDetectado}"`
                          : "(nome não identificado)"}
                      </strong>
                      {" · "}Paciente selecionado: <strong>"{paciente.nome}"</strong>
                    </div>
                    {alertaNome.observacao && (
                      <div style={{ marginTop: "4px" }}>{alertaNome.observacao}</div>
                    )}
                    <div style={{ marginTop: "4px" }}>
                      Confirme se este laudo é deste paciente antes de salvar.
                    </div>
                  </div>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1e3a8a",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  marginBottom: "14px",
                  fontSize: "13.5px",
                }}
              >
                <AlertTriangle size={16} />
                Confira cada valor contra o PDF. Desmarque o que não quiser gravar. Nada é
                salvo sem a sua confirmação.
              </div>

              {aviso && (
                <div
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    color: "#92400e",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    marginBottom: "14px",
                    fontSize: "13px",
                  }}
                >
                  ⚠️ {aviso}
                </div>
              )}

              {grupos.map((g, gi) => (
                <div
                  key={gi}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "10px",
                    marginBottom: "16px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      // B341 — cabeçalho inteiro em âmbar quando a data é suspeita:
                      // o médico precisa VER antes de rolar a lista de valores.
                      background: avaliacoesData[gi]?.suspeita
                        ? "#fffbeb"
                        : "#f9fafb",
                      padding: "10px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      flexWrap: "wrap",
                      borderBottom: avaliacoesData[gi]?.suspeita
                        ? "1px solid #fcd34d"
                        : "1px solid #e5e7eb",
                    }}
                  >
                    <strong style={{ fontSize: "13px", color: "#374151" }}>
                      Data da coleta:
                    </strong>
                    <input
                      type="date"
                      value={g.data}
                      max={hojeISO()}
                      onChange={(e) => {
                        setData(gi, e.target.value);
                        setDatasConfirmadas((c) => c.filter((i) => i !== gi));
                      }}
                      style={{
                        padding: "5px 8px",
                        border: avaliacoesData[gi]?.suspeita
                          ? "2px solid #dc2626"
                          : "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "13px",
                        fontWeight: avaliacoesData[gi]?.suspeita ? 700 : 400,
                      }}
                    />
                    <span style={{ color: "#9ca3af", fontSize: "12px", marginLeft: "auto" }}>
                      {g.linhas.filter((l) => l.incluir).length} de {g.linhas.length} marcados
                    </span>
                    {/* B341 — aviso + correção em 1 clique + confirmação explícita.
                        Ocupa a linha inteira (flexBasis 100%) abaixo do campo. */}
                    {avaliacoesData[gi]?.suspeita && (
                      <div
                        style={{
                          flexBasis: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          flexWrap: "wrap",
                          fontSize: "12.5px",
                          color: "#92400e",
                        }}
                      >
                        <span>⚠️ {avaliacoesData[gi].mensagem}</span>
                        {avaliacoesData[gi].sugestao && (
                          <button
                            type="button"
                            onClick={() =>
                              aplicarSugestaoData(
                                gi,
                                avaliacoesData[gi].sugestao as string
                              )
                            }
                            style={{
                              padding: "3px 10px",
                              background: "#2563eb",
                              color: "#fff",
                              border: "none",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            Corrigir para{" "}
                            {(avaliacoesData[gi].sugestao as string)
                              .split("-")
                              .reverse()
                              .join("/")}
                          </button>
                        )}
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={datasConfirmadas.includes(gi)}
                            onChange={(e) =>
                              setDatasConfirmadas((c) =>
                                e.target.checked
                                  ? [...c, gi]
                                  : c.filter((i) => i !== gi)
                              )
                            }
                          />
                          A data está correta
                        </label>
                      </div>
                    )}
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#fafafa", color: "#6b7280", textAlign: "left" }}>
                        <th style={{ padding: "6px 10px", width: "36px" }}></th>
                        <th style={{ padding: "6px 10px" }}>Exame</th>
                        <th style={{ padding: "6px 10px", width: "150px" }}>Valor / Resultado</th>
                        <th style={{ padding: "6px 10px", width: "80px" }}>Unidade</th>
                        <th style={{ padding: "6px 10px" }}>No PDF</th>
                      </tr>
                    </thead>
                    <tbody>
                      {BUCKETS.map(({ bucket, titulo }) => {
                        // Linhas deste bucket, preservando o índice ORIGINAL em
                        // g.linhas (os handlers toggleIncluir/setValor usam li).
                        const itens = g.linhas
                          .map((l, li) => ({ l, li }))
                          .filter((x) => x.l.bucket === bucket);
                        if (itens.length === 0) return null;
                        return (
                          <React.Fragment key={bucket}>
                            <tr>
                              <td
                                colSpan={5}
                                style={{
                                  padding: "7px 10px",
                                  background: "#eef2ff",
                                  color: "#3730a3",
                                  fontWeight: 700,
                                  fontSize: "12px",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.03em",
                                }}
                              >
                                {titulo}
                              </td>
                            </tr>
                            {itens.map(({ l, li }) => {
                              const invalido =
                                l.incluir &&
                                (l.bucket === "lab"
                                  ? parseValorLab(l.valorTexto) === null
                                  : l.valorTexto.trim() === "");
                              return (
                                <tr
                                  key={li}
                                  style={{
                                    borderTop: "1px solid #f0f0f0",
                                    opacity: l.incluir ? 1 : 0.45,
                                  }}
                                >
                                  <td style={{ padding: "5px 10px" }}>
                                    <input
                                      type="checkbox"
                                      checked={l.incluir}
                                      onChange={() => toggleIncluir(gi, li)}
                                    />
                                  </td>
                                  <td style={{ padding: "5px 10px", fontWeight: 600, color: "#374151" }}>
                                    {l.label}
                                  </td>
                                  <td style={{ padding: "5px 10px" }}>
                                    <input
                                      value={l.valorTexto}
                                      onChange={(e) => setValor(gi, li, e.target.value)}
                                      style={{
                                        width: "100%",
                                        padding: "4px 6px",
                                        border: `1px solid ${invalido ? "#ef4444" : "#d1d5db"}`,
                                        borderRadius: "5px",
                                        fontSize: "13px",
                                      }}
                                    />
                                  </td>
                                  <td style={{ padding: "5px 10px", color: "#6b7280" }}>{l.unidade}</td>
                                  <td style={{ padding: "5px 10px", color: "#9ca3af", fontSize: "12px" }}>
                                    {l.textoOriginal}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}

              {naoMapeados.length > 0 && (
                <details style={{ marginBottom: "14px" }}>
                  <summary style={{ cursor: "pointer", color: "#6b7280", fontSize: "13px" }}>
                    {naoMapeados.length} resultado(s) do PDF não reconhecido(s) pelo sistema
                    (lançar manualmente, se relevante)
                  </summary>
                  <ul style={{ margin: "8px 0 0 18px", color: "#6b7280", fontSize: "12.5px" }}>
                    {naoMapeados.map((nm, i) => (
                      <li key={i}>
                        {nm.nome}: {nm.valor} {nm.unidade}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        {/* RODAPÉ DE AÇÃO */}
        {(fase === "conferencia" || fase === "salvando") && (
          <div
            style={{
              borderTop: "1px solid #e5e7eb",
              padding: "12px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
            }}
          >
            {/* B341 — o rodapé é fixo e a lista rola: se a data suspeita está
                fora da tela, o botão cinza não explicaria nada. Aqui o motivo
                fica SEMPRE visível, ao lado do botão. */}
            <span
              style={{
                fontSize: "13px",
                color: pendenteConferirData ? "#b45309" : "#6b7280",
                fontWeight: pendenteConferirData ? 600 : 400,
              }}
            >
              {pendenteConferirData
                ? "⚠️ Confira a data destacada em vermelho antes de salvar."
                : `${totalIncluidos} valor(es) serão gravados${
                    haDataSuspeita ? " (data conferida por você)" : ""
                  }`}
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={onFechar}
                disabled={fase === "salvando"}
                style={{
                  padding: "10px 18px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={salvar}
                // B341 — trava enquanto houver data suspeita não conferida.
                disabled={
                  fase === "salvando" ||
                  totalIncluidos === 0 ||
                  pendenteConferirData
                }
                title={
                  pendenteConferirData
                    ? "Confira a data destacada em vermelho antes de salvar."
                    : undefined
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "10px 20px",
                  background:
                    totalIncluidos === 0 || pendenteConferirData
                      ? "#9ca3af"
                      : "#16a34a",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor:
                    fase === "salvando" ||
                    totalIncluidos === 0 ||
                    pendenteConferirData
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <Check size={16} />
                {fase === "salvando" ? "Salvando…" : "Confirmar e salvar"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
